import React, { forwardRef } from 'react';
import { Box, Container, Typography, Divider, styled } from '@mui/material';
import { GenomicReport as GenomicReportType, PatientInfo } from '../../types/genomic';
import { PrintablePage } from './styles';
import { ReportTitleSection } from './ReportTitle';
import { ConditionCard } from './ConditionCard';
import { ConditionDetailsSection } from './ConditionDetails';
import { Legend } from './Legend';
import { VariantCard } from './VariantCard';
import { VariantSummary } from './VariantSummary';
import { GeneVisualization } from './GeneVisualization';
import VariantOverviewTable from './VariantOverviewTable';
import { NoVariantsFound } from './NoVariantsFound';
import { Glossary } from './Glossary';
import { ACMGCriteriaSection } from './ACMGCriteriaSection';
import QueryMetricsCard from './QueryMetricsCard';

interface Props {
  report: GenomicReportType;
  profile: PatientInfo;
}

export const NoVariantsBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

/*
const LimitationItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const LimitationNumber = styled(Box)(({ theme }) => ({
  minWidth: '24px',
  height: '24px',
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.875rem',
  fontWeight: 'bold',
}));*/



const NoVariantsMessage = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(6),
  padding: theme.spacing(3),
  background: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  textAlign: 'center'
}));

const GeneBox = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  pageBreakInside: 'avoid',
}));

export const GenomicReport = forwardRef<HTMLDivElement, Props>(({ report, profile }, ref) => {
  // Group variants by gene
  const groupVariantsByGene = (variants: GenomicReportType['pathogenicVariants']) => {
    return variants.reduce((acc, variant) => {
      const gene = variant.clinvar.allele.GeneList?.Gene;

      const symbols = gene?.map((g) => g.Symbol);

      symbols?.forEach(symbol => {
        if (!acc[symbol]) {
          acc[symbol] = [];
        }
        acc[symbol].push(variant);
      });
      return acc;
    }, {} as { [key: string]: typeof variants });
  };

  const variants = [...report.pathogenicVariants, ...report.vusVariants, ...report.drugResponseVariants || []];

  // Create refs for each gene section
  const variantsRefs = React.useMemo(() => {
    const genes = Object.keys(groupVariantsByGene(variants));
    return Object.fromEntries(
      genes.map(gene => [gene, React.createRef<HTMLDivElement>()])
    );
  }, [variants]);



  return (

    <Container ref={ref} maxWidth="lg" sx={{ pb: 4 }}>
      {/* Cover Page */}

      <PrintablePage >
        <ReportTitleSection
          patient={profile}
          reportName={report.name}
          reportDescription={report.description}
          hasVariants={variants.length > 0}
        />


        {/* Variant Overview */}
        {variants.length > 0 && (
          <Box sx={{ mt: 4 }}>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              The table below summarizes all genetic variants identified in this analysis, including pathogenic variants, variants of uncertain significance (VUS), and drug response variants. Each variant is listed with its associated gene, classification, and potential impact.
            </Typography>

            <VariantOverviewTable variants={[...report.pathogenicVariants, ...report.vusVariants, ...report.drugResponseVariants || []]} />
          </Box>
        )}
        {variants.length == 0 && (
          <NoVariantsMessage>
            <Typography variant="h6" color="text.primary" gutterBottom>
              No Variants Identified
            </Typography>
            <Typography variant="body1" color="text.secondary">
              We checked {report.metrics.variantsReturned} variants and no reportable variants were identified in this analysis. This could mean that no variants meeting our reporting criteria were found in the analyzed regions, or that the variants present did not meet the threshold for reporting.
            </Typography>
          </NoVariantsMessage>
        )}

      </PrintablePage>


      {/* Conditions Overview */}
      <PrintablePage id="conditions_overview">
        <Typography variant="h2" gutterBottom color="primary">
          Conditions Overview
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          In this report we analyzed the following conditions:
        </Typography>
        {report.conditions.map((condition, index) => (
          <ConditionCard key={index} condition={condition} />
        ))}
      </PrintablePage>

      {/* Analysis Power */}
      <PrintablePage>
        <Typography variant="h2" gutterBottom color="primary">
          Analysis Power Behind Your Results
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Your genomic analysis was powered by our advanced technology platform. Below is a snapshot of the computational power that went into delivering your personalized results.
        </Typography>

        <QueryMetricsCard metrics={report.metrics} conditionsCount={report.conditions.length} />
      </PrintablePage>

      {/* Detailed Condition Analysis */}
      {report.conditions.map((condition, index) => (
        <ConditionDetailsSection key={index} condition={condition} />
      ))}


      <PrintablePage id="genetic_findings">

        {variants.length > 0 ? (

          <>
            <Typography variant="h2" gutterBottom color="primary">
              Genetic Findings
            </Typography>
            <VariantSummary variants={variants} />
            <Legend />
            {Object.entries(groupVariantsByGene(variants))
              .sort((a, b) => a[0].localeCompare(b[0]))
              .map(([gene, variants]) => (
                <GeneBox key={gene} mb={4} paddingTop={4}>
                  <Typography variant="h4" color="primary" gutterBottom>
                    {gene}
                  </Typography>
                  <Box mb={2}>
                    <GeneVisualization gene={gene} variants={variants} />
                  </Box>

                  <Box mt={2} ref={variantsRefs[gene]}>
                    {variants.map((variant, index) => (
                      <VariantCard
                        key={index}
                        variant={variant}
                        variantType="pathogenic"
                      />
                    ))}
                  </Box>
                </GeneBox>
              ))}
          </>
        ) : (

          <NoVariantsFound conditions={report.conditions} />
        )}
      </PrintablePage>


      {/* Add before the Glossary Section */}
      <Box id="acmg_criteria">
        <ACMGCriteriaSection />
      </Box>

      {/* Glossary Section */}
      <PrintablePage id="glossary">
        <Glossary />
      </PrintablePage>



      {/* Disclaimer and Processing Details */}
      <PrintablePage id="disclaimer">
        <Typography variant="h2" gutterBottom color="primary">
          Disclaimer and Processing Details
        </Typography>
        <Typography paragraph color="text.secondary">
          This report contains genetic testing results intended for non-clinical or research use. The results have been generated using the DRAGEN pipeline and should be interpreted by a qualified healthcare professional in the context of other clinical and diagnostic findings. The genetic testing performed is limited to the specific regions and types of variants that can be detected by the methods used.
        </Typography>
        <Typography paragraph color="text.secondary">
          While this test has been validated to high standards, no genetic test is 100% accurate. Negative results do not exclude all genetic conditions. A negative result means that no reportable variants were identified in the genes analyzed. There may be variants in other regions of the genome or types of variants not detectable by this test.
        </Typography>
        <Typography paragraph color="text.secondary">
          This test was developed and its performance characteristics determined by Dante Labs. It has not been cleared or approved by the FDA or any other regulatory agency.
        </Typography>
        <Divider sx={{ my: 4 }} />
        <Box sx={{ mt: 4 }}>
          <Typography variant="h3" gutterBottom>
            Processing Details
          </Typography>
          <Box sx={{ mt: 4 }}>
            <Typography variant="body1" paragraph>
              <strong>Pipeline:</strong> DRAGEN Pipeline
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Version:</strong> Dante Labs Avanti v1.0.1
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Date:</strong> {new Date().toLocaleDateString()}
            </Typography>
          </Box>
        </Box>
      </PrintablePage>
    </Container>
  );
}); 
